import Button from '../Button';
import { Text } from '../typography';
import styles from './Modal.module.scss';
import * as MuiModalComponents from '@mui/material/Modal';
import { Icon } from '@mui/material';
import { keys } from '../../utilities/translator/translation_keys';
import { useTranslation } from 'react-i18next';
import Label from '../action_items/Label';
import classNames from 'classnames';
import { formatTestId } from '../../utilities/cypress';
import Toast from './Toast';
import { useReactiveVar } from '@apollo/client';
import { toastVar, hideToast } from '../../graphql/cache/modal';

export const MODAL_CLOSE_EVENT = {
  submitted: 'SUBMITTED',
  canceled: 'CANCELED',
};

export default function Modal({
  title,
  open,
  onClose,
  children,
  variant = 'primary',
  hideCancel = false,
  hideSubmit = false,
  hideDelete = true,
  submitText,
  cancelText,
  submitDisabled = false,
  onSubmit,
  onDelete = () => {},
  allowClose = true,
  submitButtonIcon = null,
  disableCloseOnSubmit = false,
  disableCloseOnCancel = false,
  onCancel = () => {},
  footer,
  beta = false,
  subtitle = undefined,
  titleImage = undefined,
  className,
  disableClickOutside = true,
}) {
  const toastProps = useReactiveVar(toastVar);
  const MuiModal = MuiModalComponents.default;
  const { t } = useTranslation();

  const handleClose = (event, reason) => {
    if (reason === 'backdropClick' && (!allowClose || !!disableClickOutside)) {
      return;
    }
    onClose(event, reason);
  };

  return (
    <MuiModal
      className={styles.background}
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      disableAutoFocus={true}
    >
      <div className={classNames(className, styles.container)}>
        <div className={styles.header}>
          <div className={styles.titleContainer}>
            <div className={styles.leftTitle}>
              {titleImage}
              <div>
                <Text noMargin noSelect size="lg" weight="semiBold">
                  {title}
                </Text>
                <Text noMargin color="secondary" size="sm" weight="semiBold">
                  {subtitle}
                </Text>
              </div>
            </div>
            {!!beta && <Label name="Beta" color="Blue" />}
          </div>
          {allowClose && (
            <Icon
              onClick={() => onClose(null, MODAL_CLOSE_EVENT.canceled)}
              className={styles.closeIcon}
            >
              close
            </Icon>
          )}
        </div>
        <Toast {...toastProps} onHide={() => hideToast()} />
        <div className={styles.contentContainer}>{children}</div>
        <div className={styles.footer}>
          {footer && footer}
          {!hideDelete && (
            <Button
              variant="danger"
              icon="delete"
              onClick={() => {
                onDelete();
              }}
              value={t(keys.action.DELETE)}
            />
          )}
          <div className={styles.flexRowSm}>
            {!hideCancel && (
              <Button
                outlined
                icon={submitButtonIcon || 'cancel'}
                variant="secondary"
                testId={`${formatTestId(title)}-cancel`}
                onClick={() => {
                  if (!disableCloseOnCancel) {
                    onClose(null, MODAL_CLOSE_EVENT.canceled);
                  }
                  onCancel();
                }}
                value={cancelText || t(keys.action.CANCEL)}
              />
            )}
            {!hideSubmit && (
              <Button
                disabled={submitDisabled}
                variant={variant}
                value={submitText || t(keys.action.SUBMIT)}
                icon={submitButtonIcon || 'check_circle'}
                testId={`${formatTestId(title)}-submit`}
                onClick={() => {
                  onSubmit();
                  if (!disableCloseOnSubmit) {
                    onClose(null, MODAL_CLOSE_EVENT.submitted);
                  }
                }}
              />
            )}
          </div>
        </div>
      </div>
    </MuiModal>
  );
}
